import { memberEventId, type FbEventOptions } from '@arthelper/shared/facebook-pixel';
import { initiateCheckoutPendingKey } from '../constants/onboarding';

export function initiateCheckoutDedupKey(asfId: string) {
  return `fb_initiate_checkout_${asfId}`;
}

const COMPLETED_RECENTLY_MS = 5 * 60 * 1000;
/** Max time after completion we still honor a deferred pending intent (e.g. tab closed before asfId). */
const PENDING_DEFERRAL_MS = 24 * 60 * 60 * 1000;

export function isOnboardingCompletedRecently(completedAt: string | null | undefined): boolean {
  if (!completedAt) return false;
  return Date.now() - new Date(completedAt).getTime() < COMPLETED_RECENTLY_MS;
}

function isWithinPendingDeferralWindow(completedAt: string | null | undefined): boolean {
  if (!completedAt) return false;
  return Date.now() - new Date(completedAt).getTime() < PENDING_DEFERRAL_MS;
}

type FbPixel = {
  track: (event: 'InitiateCheckout', params?: Record<string, unknown>, options?: FbEventOptions) => void;
};
type Gtm = { push: (payload: { event: 'completed_onboarding'; user_id: string }) => void };

function getStorageItem(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function setStorageItem(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    /* private mode / quota */
  }
}

function removeStorageItem(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    /* private mode / quota */
  }
}

/** Pending survives tab close (localStorage); migrate legacy sessionStorage entries. */
function hasDeferredPendingIntent(sessionId: string, refPending: boolean): boolean {
  if (refPending) return true;

  const key = initiateCheckoutPendingKey(sessionId);
  if (getStorageItem(localStorage, key) === 'true') return true;

  if (typeof sessionStorage !== 'undefined' && getStorageItem(sessionStorage, key) === 'true') {
    setStorageItem(localStorage, key, 'true');
    removeStorageItem(sessionStorage, key);
    return true;
  }

  return false;
}

function setDeferredPendingIntent(sessionId: string): void {
  setStorageItem(localStorage, initiateCheckoutPendingKey(sessionId), 'true');
}

function clearDeferredPendingIntent(sessionId: string): void {
  const key = initiateCheckoutPendingKey(sessionId);
  removeStorageItem(localStorage, key);
  if (typeof sessionStorage !== 'undefined') {
    removeStorageItem(sessionStorage, key);
  }
}

function hasFiredInitiateCheckout(asfId: string): boolean {
  const key = initiateCheckoutDedupKey(asfId);
  if (getStorageItem(localStorage, key) === 'true') return true;

  if (typeof sessionStorage !== 'undefined' && getStorageItem(sessionStorage, key) === 'true') {
    setStorageItem(localStorage, key, 'true');
    removeStorageItem(sessionStorage, key);
    return true;
  }

  return false;
}

function markInitiateCheckoutFired(asfId: string): void {
  setStorageItem(localStorage, initiateCheckoutDedupKey(asfId), 'true');
  if (typeof sessionStorage !== 'undefined') {
    removeStorageItem(sessionStorage, initiateCheckoutDedupKey(asfId));
  }
}

function fireInitiateCheckoutOnce(asfId: string, sessionId: string, fbPixel: FbPixel, gtm: Gtm): boolean {
  try {
    if (!hasFiredInitiateCheckout(asfId)) {
      // Same eventID as the API's Conversions API copy, so Meta keeps one.
      fbPixel.track('InitiateCheckout', {}, { eventID: memberEventId('InitiateCheckout', asfId) });
      gtm.push({ event: 'completed_onboarding', user_id: asfId });
      markInitiateCheckoutFired(asfId);
    }
    clearDeferredPendingIntent(sessionId);
    return true;
  } catch {
    return false;
  }
}

export function tryFireInitiateCheckout(
  asfId: string,
  sessionId: string,
  fbPixel: FbPixel,
  gtm: Gtm,
  gate: {
    justCompleted?: boolean;
    pixelPendingRef?: boolean;
    completedAt?: string | null;
    completedRecently?: boolean;
  }
): boolean {
  const completedRecently = gate.completedRecently ?? isOnboardingCompletedRecently(gate.completedAt);

  const pendingIntent = hasDeferredPendingIntent(sessionId, gate.pixelPendingRef ?? false);

  if (pendingIntent && gate.completedAt && !isWithinPendingDeferralWindow(gate.completedAt)) {
    clearDeferredPendingIntent(sessionId);
    if (!gate.justCompleted && !completedRecently) {
      return false;
    }
  }

  const pendingStillValid = pendingIntent && !!gate.completedAt && isWithinPendingDeferralWindow(gate.completedAt);

  if (!gate.justCompleted && !completedRecently && !pendingStillValid) {
    return false;
  }

  return fireInitiateCheckoutOnce(asfId, sessionId, fbPixel, gtm);
}

/** Fire when onboarding completion was deferred or the user left /welcome early (any route). */
export function tryFireDeferredInitiateCheckout(
  asfId: string,
  sessionId: string,
  fbPixel: FbPixel,
  gtm: Gtm,
  completedAt?: string | null
): boolean {
  return tryFireInitiateCheckout(asfId, sessionId, fbPixel, gtm, { completedAt });
}

export { setDeferredPendingIntent };
